import type { CreateReportSchema } from '@/entities/report'
import type { FilterSchema } from '@/shared/components/filter/validation'
import type { SavedFilterType } from '@/shared/generated'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { InfoIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	createReportSchema,
	useCreateSavedFilterMutation,
} from '@/entities/report'
import { Access } from '@/entities/session'

import {
	FormProvider,
	RHFCheckbox,
	RHFTextField,
} from '@/shared/components/HookForm'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import { FieldGroup, FieldSet } from '@/shared/components/ui/field'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import { ampli } from '@/shared/lib/ampli'
import Sentry from '@/shared/lib/sentry'
import {
	actCreated,
	errorMessage,
	processingMessage,
	successActionMessage,
} from '@/shared/tools/message'

type CreateReportModal = {
	filters: FilterSchema[]
	type: SavedFilterType
	isOpen: boolean
	close(): void
}

export function CreateReportModal({
	isOpen,
	close,
	type,
	filters,
}: CreateReportModal) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const createSavedFilter = useCreateSavedFilterMutation({
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ['saved-filters'],
			}),
	})

	const isPending = createSavedFilter.isPending
	const methods = useForm({
		resolver: zodResolver(createReportSchema),
		defaultValues: {
			name: '',
			type,
			is_public: false,
			filters: filters || [],
		},
	})

	const _handleSubmit = async (body: CreateReportSchema) => {
		ampli.reportsCreateReport({
			'Is global': body.is_public,
		})
		const result = toast.promise(createSavedFilter.mutateAsync(body), {
			loading: processingMessage(t),
			success: successActionMessage('Report', actCreated(t), t),
			error: (error) => {
				Sentry.captureException(error)

				return errorMessage(t)
			},
		})
		await result.unwrap()
		close()
	}
	const handleSubmit = methods.handleSubmit(_handleSubmit)

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent>
				<ModalHeader>
					<ModalTitle>{t('saved_reports.create.modal.title')}</ModalTitle>
					<ModalDescription className='sr-only'>
						Create a new filter
					</ModalDescription>
				</ModalHeader>
				<ModalBody className='flex flex-col'>
					<FormProvider
						methods={methods}
						onSubmit={handleSubmit}
						className='grid gap-3'
					>
						<FieldSet>
							<FieldGroup>
								<RHFTextField
									disabled={isPending}
									required
									label={t('report_name')}
									name='name'
									className='w-full'
								/>
								<Access permissions={['MANAGE_GLOBAL_FILTERS']}>
									<RHFCheckbox
										disabled={isPending}
										label={t('make_visible_for_all')}
										name='is_public'
									/>
								</Access>
							</FieldGroup>
							<Alert variant='info'>
								<InfoIcon />
								<AlertDescription>
									{t('filter.input.is_global.hint', {
										defaultValue:
											'Making saved filter public will make it available for use by other users.',
									})}
								</AlertDescription>
							</Alert>
						</FieldSet>
					</FormProvider>
				</ModalBody>
				<ModalFooter className='justify-between'>
					<ModalClose asChild>
						<Button>{t('general_cancel')}</Button>
					</ModalClose>
					<Button variant='primary' disabled={isPending} onClick={handleSubmit}>
						{isPending && <Spinner />}
						{t('general_save_and_apply')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}
